import { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Smartphone, ArrowLeft, QrCode, AlertCircle } from 'lucide-react';
import { useAgent } from '../../context/AgentContext';
import Button from '../../components/Button';

export default function StepConnect() {
  const { goNext, goPrev } = useOutletContext();
  const { currentAgent, startWhatsAppPairing, isLoading, error, clearError } = useAgent();
  const [localError, setLocalError] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState('');

  // Validate phone number format (must start with + and have digits)
  const isValidPhoneNumber = (phone) => {
    const phoneRegex = /^\+[1-9]\d{6,14}$/;
    return phoneRegex.test(phone.replace(/\s/g, ''));
  };

  const handleRequestQR = async () => {
    if (!currentAgent?.id) {
      setLocalError('No agent found. Please go back and create an agent first.');
      return;
    }

    // Validate phone number
    const cleanPhone = phoneNumber.replace(/\s/g, '');
    if (!cleanPhone) {
      setLocalError('Please enter your WhatsApp phone number.');
      return;
    }

    if (!isValidPhoneNumber(cleanPhone)) {
      setLocalError('Please enter a valid phone number in international format (e.g., +254712345678)');
      return;
    }

    setLocalError(null);
    clearError();

    try {
      const result = await startWhatsAppPairing(cleanPhone);

      // If already connected, show success and move forward
      if (result.status === 'connected') {
        goNext();
        return;
      }

      // If pairing initiated successfully, move to QR code step
      if (result.status === 'pairing' || result.qr_image_url) {
        goNext();
      }
    } catch (err) {
      setLocalError(err.message || 'Failed to start WhatsApp pairing');
    }
  };

  const displayError = localError || error;

  return (
    <div className="p-8 max-w-2xl mx-auto animate-fade-in">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 rounded-xl bg-gradient-to-br from-primary to-accent">
            <Smartphone className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-text-primary">Connect WhatsApp</h1>
            <p className="text-text-secondary">Link your WhatsApp Business number</p>
          </div>
        </div>
      </div>

      <div className="card p-6">
        {/* WhatsApp Icon */}
        <div className="flex justify-center mb-6">
          <div className="p-6 rounded-3xl bg-green-50">
            <svg className="w-16 h-16 text-green-500" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
          </div>
        </div>

        <div className="text-center mb-6">
          <h2 className="text-lg font-semibold text-text-primary mb-2">
            Ready to connect your WhatsApp?
          </h2>
          <p className="text-text-secondary text-sm">
            Enter your phone number and generate a QR code to scan with your WhatsApp app.
          </p>
        </div>

        {/* Phone Number Input */}
        <div className="mb-4">
          <label htmlFor="phoneNumber" className="block text-sm font-medium text-text-primary mb-2">
            WhatsApp Phone Number
          </label>
          <input
            type="tel"
            id="phoneNumber"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            placeholder="+254712345678"
            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
          />
          <p className="mt-1.5 text-xs text-text-muted">
            Enter in international format with country code (e.g., +254 for Kenya, +255 for Tanzania, +256 for Uganda)
          </p>
          <p className="mt-1 text-xs text-amber-600 flex items-center gap-1">
            <span className="inline-block w-1 h-1 rounded-full bg-amber-500"></span>
            This number must already be active on WhatsApp
          </p>
        </div>

        {/* Error Display */}
        {displayError && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-red-700 text-sm font-medium">Connection Error</p>
              <p className="text-red-600 text-sm">{displayError}</p>
            </div>
          </div>
        )}

        <Button
          className="w-full"
          icon={QrCode}
          onClick={handleRequestQR}
          loading={isLoading}
          disabled={!currentAgent?.id || !phoneNumber.trim()}
        >
          {isLoading ? 'Generating QR Code...' : 'Generate QR Code'}
        </Button>

        {!currentAgent?.id && (
          <p className="text-amber-600 text-sm text-center mt-3">
            Please complete the previous steps first to create your agent.
          </p>
        )}
      </div>

      {/* Instructions */}
      <div className="mt-6 card p-6">
        <h3 className="font-semibold text-text-primary mb-4">How to connect</h3>
        <ol className="space-y-3 text-sm text-text-secondary">
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center">1</span>
            <span>Click "Generate QR Code" above</span>
          </li>
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center">2</span>
            <span>Open WhatsApp on your phone</span>
          </li>
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center">3</span>
            <span>Go to Settings &gt; Linked Devices &gt; Link a Device</span>
          </li>
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center">4</span>
            <span>Scan the QR code with your phone</span>
          </li>
        </ol>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between mt-8">
        <Button
          variant="ghost"
          icon={ArrowLeft}
          onClick={goPrev}
        >
          Back
        </Button>
        <Button
          variant="ghost"
          onClick={goNext}
        >
          Skip for now
        </Button>
      </div>
    </div>
  );
}